import {
  BookerCalendarEvent,
  BookMe,
  BookMeeting,
  CurrentBook,
  MemberInfo,
  PickedMeetingLocation,
} from '@chilipiper/api-type-def'
import { DotsLoader, Flex, Text, ThemeProviderClean } from '@chilipiper/design-system'
import { getDomain, parseQuery, stringifyQuery } from '@chilipiper/utils'
import { defaultLocale, Locale, useLocale, useTranslations } from '@chilipiper/locale'
import '@chilipiper/booking-flow-legacy/src/old/availability-mobile.css'
import '@chilipiper/booking-flow-legacy/src/old/prospect-form.css'
import {
  BookWith,
  findFirstMember,
  getMember,
  convertEventsTime,
  getAvailabilityData,
  book,
  loadAggregated,
  loadAvailableEventsAsync,
  loadBookMe,
  loadMembersAsync,
  reschedule,
  PreviousPick,
  TimeSection,
  Days,
  IntercomData,
  FaultSettings,
} from '@chilipiper/booking-flow-legacy'
import timeConverter from '@chilipiper/meetings-components/src/time-utils/TimeConverter'
import timezoneFinder from '@chilipiper/meetings-components/src/time-utils/TimezoneFinder'
import { defaultErrorCallback, get } from '@chilipiper/service/src/api'
import { WorkspaceProvider } from '@chilipiper/workspace'
import classnames from 'classnames'
import { countBy, filter, find, some } from 'lodash'
import moment, { Moment } from 'moment-timezone'
import React, { lazy } from 'react'
import { withGuestContext } from '../app/guest-app/guestContext'
import { GuestContext } from '../app/guest-app/types'
import AutoBook from './AutoBook'
import './availability.css'
import './flow.css'
import { getAdvanceDays } from './getAdvanceDays'
import { isMeetingApprovalFlow, redirect } from './helpers/helpers'

const ProspectFormWithValidation = lazy(() => import('./prospect/ProspectForm'))
const OneClickBookingIntermediaryScreen = lazy(
  () => import('./components/book/OneClickBookingIntermediaryScreen')
)
const MeetingLocationPage = lazy(
  () => import('@chilipiper/booking-flow-legacy/src/old/meeting-location/MeetingLocationPage')
)

export interface DataType {
  assigneesIds: string[]
  assigneesInfo: any[]
  bookingId: string | null
  end: number
  guests?: string[]
  meetingDuration: number
  meetingEnd: Moment
  meetingStart: Moment
  queueId?: string
  roomsIds: string[]
  routeId?: string
  slot: number
  start: number
  step: number
  template: string
  timezone: string
  withAvailableDays: boolean
  withBuffers: boolean
  withDelay: boolean
}

interface Props extends Record<string, any> {
  //TODO: Record<string, any> allows basically anything
  fault: (settings: FaultSettings) => void
  guestContext: GuestContext
  intercomData?: IntercomData | null
  locale: Locale
}

interface State extends Record<string, any> {
  assigneesInfo: MemberInfo[]
  bookingId: string
  bookme?: CurrentBook
  date: Moment
  events: BookerCalendarEvent[]
  guests: string[]
  isRescheduling: boolean
  members: MemberInfo[]
  queue: any
  routeId: string
  timezone: string
}

const Availability = (props: Omit<Props, 'locale'>) => {
  const locale = useLocale()
  return <AvailabilityWithoutData locale={locale} {...props} fault={props.fault} />
}

export class AvailabilityWithoutLocale extends React.Component<Props, State> {
  now = moment()

  q = parseQuery()

  autoBook = new AutoBook()

  constructor(props: Props) {
    super(props)
    this.state = {
      debug: this.q.hiddendebug,
      showBack: this.q.back,
      loading: true,
      loadingFlower: false,
      timeLoading: true,
      timezone: timezoneFinder.timezone(),
      date: this.startDate(),
      time: undefined,
      queue: undefined,
      events: [],
      assigneeId: undefined,
      confirmation: false,
      with: this.q.with,
      members: [],
      settings: {},
      template: {},
      daysMax: 35,
      isRescheduling: !!this.q.rescheduleId,
      assigneesInfo: [],
      prospectId: this.q.id,
      marketing: this.q.marketing === 'true',
      hide_title: this.q.hide_title === 'true',
      routeId: this.q.routeId,
      title: this.q.title,
      bookingId: this.q.bookingId,
      selectedMeetingType: this.q.selectedMeetingType,
      seriesId: this.q.seriesId,
      salesforceAccountId: this.q.accountId,
      sortedRooms: [],
      noMemberFound: false,
      guests: [],
      meetingLocationScreen: false,
      pickedMeetingLocation: undefined,
      showOneClickBookingIntermediaryScreen: false,
    }
  }

  componentDidMount() {
    const { history, guestContext } = this.props

    if (guestContext.isIntermediate) {
      return redirect(history, {
        ...guestContext.location,
        pathname: `/book/intermidiate`,
      })
    }
    this.setMeetingType()
    this.setLinkProperties()
    loadAggregated(
      result => {
        if (result?.queue?.id) {
          this.findRoomId(result.queue.id)
        }
        this.loadAssigneeName(result?.bookme)
        this.setSettings(result, () => {
          this.setAdvancePeriod()
          this.setState(result as any, this.refresh)
          this.setState({ loading: false })
        })
      },
      guestContext.intercomData?.template ? { ...guestContext.intercomData } : null,
      this.fault
    )

    window.parent?.postMessage({ action: 'SetIframeSize', args: { width: 560, height: 810 } }, '*')
  }

  componentDidUpdate() {
    const { guestContext, history } = this.props

    if (guestContext.component === 'another-tab') {
      return redirect(history, {
        ...guestContext.location,
        pathname: `/book/another-tab`,
      })
    }
  }

  findRoomId(queueId: string) {
    get(`rooms/public-sort/${queueId}`)
      .json()
      .then(result => {
        this.setState({ sortedRooms: result })
      })
      .catch(defaultErrorCallback)
  }

  loadAssigneeName = (bookMe?: CurrentBook) => {
    const id = bookMe?.bookMeeting?.assigneeId
    if (id) {
      get(`templates/details/name/${id}`)
        .json<{ name?: string }>()
        .then(result => {
          if (result.name && result.name.length > 0) {
            this.setState({ assigneeName: result.name })
          }
        })
        .catch(defaultErrorCallback)
    }
  }

  setMeetingType = () => {
    if (this.state.selectedMeetingType !== undefined) {
      get(
        `route/set-meeting-type/${getDomain()}/${this.state.routeId}/${
          this.state.selectedMeetingType
        }`
      ).catch(defaultErrorCallback)
    }
  }

  setLinkProperties = () => {
    // TODO: Component is mounted before window.location is changed? How is that possible?
    const { search } = this.props.location
    const query = parseQuery(search)

    this.setState({
      showBack: query.back,
      with: query.with,
      prospectId: query.id,
      marketing: query.marketing === 'true',
      hide_title: query.hide_title === 'true',
      routeId: query.routeId,
      title: query.title,
      bookingId: query.bookingId,
      objectId: query.objectId,
    })
  }

  startDate = (): Moment => {
    const date = moment()
    let add = 0
    if (date.day() === 0) {
      add = 1
    } else if (date.day() === 6) {
      add = 2
    }
    date.add(add, 'days')
    return date
  }

  firstAvailableDay(events: BookerCalendarEvent[]) {
    const result = this.state.date ? moment(this.state.date) : this.getStartDate()

    if (events.length <= 0) {
      return result
    }
    const end = moment(result).add(this.state.daysMax, 'days')
    while (result.isSameOrBefore(end, 'day')) {
      const hasEvents = events.some(event => moment(event.start).isSame(result, 'days'))
      if (hasEvents) {
        break
      }
      result.add(1, 'days')
    }
    return result
  }

  setAdvancePeriod() {
    const { settings } = this.state
    if (settings.specificDate) {
      const diff = moment(settings.dateMeetingEnd).diff(settings.dateMeetingStart, 'days')
      this.setState({
        daysMax: diff,
        date: moment(settings.dateMeetingStart),
      })
    } else {
      const period = settings.advancePeriod || 'Week'
      const value = settings.advanceValue || 6
      this.setState({ daysMax: getAdvanceDays(period, value) })
    }
  }

  setSettings = (data: any, callback: () => void) => {
    const settings = data?.settings ? data?.settings : (data?.template?.availability ?? {})
    settings.loaded = true
    const state: any = { settings: settings }
    if (settings.showTimezoneToProspect && settings.timeZone) {
      state.timezone = settings.timeZone
    }
    this.setState(state, callback)
  }

  setRescheduling = () => {
    const bookme = this.state.bookme
    if (bookme?.isBooked) {
      this.setState({ isRescheduling: true })
    }
  }

  changeRouteStatus = (status: boolean) => {
    if (this.state.routeId) {
      get(`route/change-status/${this.state.routeId}/${status}`).catch(defaultErrorCallback)
    }
  }

  refresh() {
    this.setRescheduling()
    this.setState({ timeLoading: true, loadMembers: true, loadSlots: true }, () => {
      const data = this.data()
      Promise.all([loadMembersAsync(data), loadAvailableEventsAsync(data)])
        .then(([members, { result: events }]) => {
          let nextState = {}
          if (!members || members.length === 0) {
            this.fault({ message: 'book-me/info did not return members', data: this.data() })
          } else {
            nextState = {
              ...nextState,
              assigneesInfo: members,
              members: this.mergeAssigneesInfo(members),
            }
            // actually it's fine to do it in the callback
            // eslint-disable-next-line @eslint-react/no-access-state-in-setstate
            const { bookme, queue } = this.state
            // actually it's fine to do it in the callback
            // eslint-disable-next-line @eslint-react/no-access-state-in-setstate
            const [convertedEvents, availableDays] = convertEventsTime(events, this.state.timezone)

            const filteredEvents = convertedEvents.filter(slot => {
              return getMember(queue, slot, bookme?.bookMeeting?.assigneeId)
            })

            this.changeRouteStatus(filteredEvents.length > 0)
            const firstDay = this.firstAvailableDay(filteredEvents)

            this.setState(
              {
                ...nextState,
                loadMembers: false,
                loadSlots: false,
                date: firstDay,
                events: filteredEvents,
                availableDays,
              },
              this.postRefreshAction
            )
          }
        })
        .catch(error => {
          defaultErrorCallback(error)
          this.fault({
            message: 'refresh call failed (loadMembers or loadSlots or change route status)',
            data,
          })
        })
    })
  }

  isThereMembersAvailable = () => {
    const { bookme, events, queue } = this.state

    return events.some(slot => getMember(queue, slot, bookme?.bookMeeting?.assigneeId))
  }

  postRefreshAction = () => {
    if (!this.state.loadMembers && !this.state.loadSlots) {
      this.setState({ timeLoading: false })
      const hasMembers = this.isThereMembersAvailable()
      if (!hasMembers) {
        this.setState({ noMemberFound: true })

        return
      }
      // Select slot automatically
      if (this.autoBook.shouldAutoBook()) {
        this.onTimeChange(this.autoBook.dateTime)
      }
    }
  }

  getSlotSize = (): number => {
    const { slotSize } = this.state.settings
    if (slotSize) {
      return slotSize
    }

    const duration = this.state.bookme?.bookMeeting.duration || 15
    return duration < 30 ? 15 : 30
  }

  getTimezone = () => {
    const assigneeId = this.state.bookme?.bookMeeting.assigneeId
    const info = assigneeId && this.state.assigneesInfo?.find(i => i.id === assigneeId)
    const assigneeTimezone =
      (info ? info.timezone : this.state.bookme?.bookMeeting.timezone) ?? 'UTC'
    return this.state.settings.respectAssigneeTimezone
      ? assigneeTimezone
      : this.state.settings.timeZone
  }

  getStartDate = (): Moment => {
    const availability = this.state.settings
    if (availability.specificDate) {
      const timezone = this.getTimezone()
      const startMoment = moment(availability.dateMeetingStart).clone().tz(timezone)
      let start = startMoment.add(
        moment(availability.dateMeetingStart).utcOffset() - startMoment.utcOffset(),
        'minutes'
      )
      start = timeConverter.convertTimezoneToLocal(start, this.state.timezone)
      return start
    }
    return moment().startOf('day')
  }

  getEndDate = (start: Moment) => {
    const { state } = this
    const availability = state.settings
    if (availability.specificDate) {
      const timezone = this.getTimezone()
      const endMoment = moment(availability.dateMeetingEnd).clone().tz(timezone)
      let end = endMoment
        .add(moment(availability.dateMeetingEnd).utcOffset() - endMoment.utcOffset(), 'minutes')
        .add(1, 'days')
        .add(-1, 'seconds')
      end = timeConverter.convertTimezoneToLocal(end, this.state.timezone)
      return end
    }
    return moment(start)
      .add(this.state.daysMax + 1, 'days')
      .add(-1, 'seconds')
  }

  getMeetingStart = (): Moment => {
    const { settings } = this.state
    return settings.specificDate ? moment(settings.dateMeetingStart) : moment().startOf('day')
  }

  getMeetingEnd = () => {
    const { settings } = this.state
    return settings.specificDate
      ? moment(settings.dateMeetingEnd)
      : moment(this.getMeetingStart())
          .add(this.state.daysMax + 1, 'days')
          .add(-1, 'seconds')
  }

  data = (): DataType => {
    const start = this.getStartDate()
    const end = this.getEndDate(start)
    const meetingStart = this.getMeetingStart()
    const meetingEnd = this.getMeetingEnd()
    return getAvailabilityData({
      ...this.state,
      ...this.props,
      assigneeId: this.q.assigneeId as string | null,
      slot: this.getSlotSize(),
      start,
      end,
      meetingStart,
      meetingEnd,
    })
  }

  // TODO : Copy-Paste from Loader.js - remove - add Timezone to Loader
  mergeAssigneesInfo(infos: any): MemberInfo[] {
    const { queue } = this.state
    let members = queue
      ? filter(queue.members, m => some(infos, assignee => assignee.id === m.id))
      : [{ id: this.state.bookme?.bookMeeting.assigneeId }]
    members.forEach(member => {
      const info = find(infos, a => a.id === member.id) || {}
      if (info) {
        member.order = info.order
        member.accessible = info.accessible
        member.timezone = info.timezone
        if (!queue || queue.algorithm !== 'Ownership') {
          member.onVacation = info.onVacation
        }
        member.available = !info.busy
      }
    })
    members = filter(members, m => m.accessible)
    return members
  }

  same = (d1: Moment, d2: Moment) => d1.isSame(d2, 'minutes') && d1.isSame(d2, 'seconds')

  onDayChange = (date: Moment) =>
    this.setState({ date: date, time: undefined, assigneeId: undefined })

  onTimeChange = (time: Moment, slot?: any) => {
    if (slot?.sequentials) {
      this.setState({ sequentials: slot.sequentials })
    }

    time = timeConverter.convertTimezoneFromLocal(time, this.state.timezone)

    const { bookme, queue, template } = this.state

    const first = getMember(queue, slot, bookme?.bookMeeting?.assigneeId)
    if (!first) {
      this.setState({ noMemberFound: true })
      return
    }
    const roomsIds = slot?.roomsIds ?? []
    const matchingRoom = this.state.sortedRooms.find((room: any) => roomsIds.includes(room.roomId))
    const roomId = matchingRoom ? matchingRoom.roomId : undefined

    this.setState({ time: time, assigneeId: first.id, roomId: roomId }, () => {
      // actually it's fine to do it in the callback
      // eslint-disable-next-line @eslint-react/no-access-state-in-setstate
      if (this.state.isRescheduling) {
        const meetingLocationType = bookme?.bookMeeting.pickedMeetingLocation?.type
        if (meetingLocationType === 'AskTheGuest' || meetingLocationType === 'CallTheGuest') {
          this.setState({ meetingLocationScreen: true })
        } else {
          this.setState({ loadingFlower: true }, this.reschedule)
        }
      } else if (
        // actually it's fine to do it in the callback
        // eslint-disable-next-line @eslint-react/no-access-state-in-setstate
        (bookme?.bookMeeting.id || this.state.marketing || this.state.bookingId) &&
        !template?.oneClickBookingRequiresFormAnyway
      ) {
        this.confirm(true)
      } else if (this.q.conciergeFormFilled === 'true' || this.q.marketing === 'true') {
        this.confirm(true)
      } else {
        this.setState({ confirmation: true })
      }
    })
  }

  onZoneChange = (timezone: string) => this.setState({ timezone: timezone }, this.refresh)

  confirm = (result: boolean, prospect?: any, pickedMeetingLocation?: PickedMeetingLocation) => {
    const { inIntercom, seriesStatus } = this.state
    let { marketingId } = this.state
    if (!result && inIntercom) {
      ;(window as any).INTERCOM_MESSENGER_SHEET_LIBRARY.closeSheet()
    }
    const time =
      seriesStatus?.meetingTimes && seriesStatus.meetingTimes.length === 1
        ? this.state.time
        : undefined

    marketingId = marketingId || prospect?.marketingId
    this.setState(
      {
        confirmation: false,
        prospectInfo: prospect,
        marketingId: marketingId,
        pickedMeetingLocation,
      },
      () => {
        if (
          result &&
          !pickedMeetingLocation &&
          // actually it's fine to do it in the callback
          // eslint-disable-next-line @eslint-react/no-access-state-in-setstate
          this.state.template?.possibleMeetingLocations?.length > 1
        ) {
          this.setState({ showOneClickBookingIntermediaryScreen: true })
        } else if (result) {
          this.setState({ loadingFlower: true }, this.book)
        } else {
          this.setState({ time: time, assigneeId: undefined }, (this as any).onProspectRefused)
        }
      }
    )
  }

  inviteProspect = () => {
    return !this.state.template.withoutProspect
  }

  reschedule = () => {
    const { time, pickedMeetingLocation, bookme, assigneesInfo, assigneeId, template } = this.state

    const defaultData = {
      description: '',
      guests: [],
      invitees: [],
      isLead: false,
      location: '',
      mandatoryGuests: [],
      multipleSelection: [],
      notifyGuests: false,
      sequentials: [],
      title: '',
      assigneesInfo: [],
      online: false,
    }

    const startDate = this.state.time.valueOf()
    const endDate = moment(time).add(this.getDuration(), 'minutes').valueOf()

    const dataToReschedule = {
      ...defaultData,
      ...(bookme?.bookMeeting ?? {}),
      bookerInfo: bookme?.bookMeeting.bookerInfo
        ? {
            ...(bookme.bookMeeting.bookerInfo ?? {}),
            start: startDate,
            end: endDate,
          }
        : undefined,
      startDate,
      endDate,
      assigneesInfo,
      assigneeId,
      rawDetails: {
        description: template?.description ?? bookme?.bookMeeting?.description,
        location: template?.location ?? bookme?.bookMeeting?.location,
        title: template?.title ?? bookme?.bookMeeting?.title,
      },
      reportId: bookme?.bookResult?.reportId,
      timezone: this.state.timezone,
      inviteProspect: this.inviteProspect(),
    }
    if (pickedMeetingLocation) {
      dataToReschedule.pickedMeetingLocation = pickedMeetingLocation
    }

    reschedule(dataToReschedule, this.booked, this.fault)
  }

  confirmRescheduling = (pickedMeetingLocation: PickedMeetingLocation) => {
    this.setState({ pickedMeetingLocation, loadingFlower: true }, this.reschedule)
  }

  bookWithIntermediaryScreen = (pickedMeetingLocation: PickedMeetingLocation) => {
    this.setState({ pickedMeetingLocation, loadingFlower: true }, this.book)
  }

  book = () => {
    const {
      time,
      bookme,
      prospectInfo,
      assigneeId,
      timezone,
      assigneesInfo,
      routeId,
      roomId,
      bookingId,
      objectId,
      prospectId,
      seriesId,
      marketingId,
      guests,
      pickedMeetingLocation,
      template,
    } = this.state
    const { intercomData, singleUseLinkData } = this.props.guestContext
    const defaultData = {
      description: '',
      guests: [],
      invitees: [],
      isLead: false,
      location: '',
      mandatoryGuests: [],
      multipleSelection: [],
      notifyGuests: false,
      sequentials: [],
      title: '',
      online: false,
    }
    const overrideData = {
      assigneesInfo: assigneesInfo ?? [],
      endDate: moment(time).add(this.getDuration(), 'minutes').valueOf(),
      inviteProspect: this.inviteProspect(),
      startDate: moment(time).valueOf(),
      assigneeId,
      timezone: intercomData?.timezone ? intercomData.timezone : timezone,
      routeId,
      roomId,
      marketingId,
    }
    const data: BookMeeting = {
      ...defaultData,
      ...(bookme?.bookMeeting ?? {}),
      ...overrideData,
    }
    if (intercomData?.duration) {
      data.duration = intercomData.duration
    }
    if (guests.length) {
      data.invitees = (data.invitees ?? []).concat(guests.map(guest => ({ email: guest })))
    }
    const meetingApprovalFlow = isMeetingApprovalFlow(
      template?.meetingApprovalFlow,
      this.q.cameFromCompose === 'true'
    )

    if (singleUseLinkData?.id) {
      data.singleUseLinkId = singleUseLinkData.id
    }

    data.pickedMeetingLocation = pickedMeetingLocation

    if (bookingId) {
      data.bookingId = bookingId
    }
    if (objectId) {
      data.relateTo = objectId
    }

    if (prospectInfo) {
      data.id = data.id || prospectInfo.id
      data.prospect = prospectInfo.email
      data.prospectPhone = prospectInfo.phone
      data.prospectName = prospectInfo.name
    }

    if (!data.id && prospectId) {
      data.id = prospectId
    }

    if (this.state.with) {
      data.prospect = this.state.with
    }
    if (seriesId) {
      data.meetingSeriesId = seriesId
    }

    const { queue } = this.state
    if (queue) {
      const first = findFirstMember(queue)
      data.orderPositions = { firstInQueueId: first?.id }
      const sequentials = this.state.sequentials.map((e: any) => {
        const assignee = {
          assigneeId: find(queue.members, m => m.id === e.assigneeId)?.id,
          start: e.start.valueOf(),
          end: e.end.valueOf(),
          info: {
            title: data.title,
            location: data.location,
            description: data.description,
          },
        }
        return assignee
      })
      data.sequentials = sequentials
    }

    // Don't check available for auto book
    const extensionData = this.data()
    data.bookerInfo = {
      ...(data.bookerInfo ?? { assigneesGroupIds: [] }),
      ...extensionData,
      assigneesIds: (this.state.members ?? []).map(a => a.id) as string[],
    }
    data.bookingUrl = window.location.href

    data.prospectValues = prospectInfo
    book({
      bookme: data,
      success: meetingApprovalFlow ? this.requested : this.booked,
      fault: (data: any) => this.fault(data),
      precheck: !this.autoBook.shouldAutoBook(),
      isMeetingApprovalFlow: meetingApprovalFlow,
      useProspectHack: this.q.id
        ? {
            id: this.q.id,
            campaignId: this.q.campaignId,
            status: this.q.status,
          }
        : undefined,
    })
  }

  openCalendar = () => {
    loadBookMe(bookme => {
      redirect(this.props.history, {
        pathname: `${this.props.guestContext.uri}/calendar`,
        state: bookme?.bookMeeting || {},
      })
    }, this.fault)
  }

  fault = (data: { data?: Partial<BookMe> | DataType; message?: string }) => {
    this.props.guestContext.fault({
      message: '',
      ...data,
      isIntercom: this.state.isIntercom,
      bookMeId: this.state?.bookme?.id,
      redirectProps: { back: this.openCalendar },
    })
  }

  goHome = () => {
    window.location.href = 'https://www.chilipiper.com'
  }

  getDuration = (): number =>
    (this.props.guestContext.intercomData || this.state.bookme?.bookMeeting)?.duration ?? 30

  booked = () => {
    const search = stringifyQuery(this.q)
    redirect(this.props.history, {
      search,
      pathname: `${this.props.guestContext.uri}/booked`,
      props: { uri: this.props.guestContext.uri },
    })
  }

  requested = ({ booked }: any) => {
    redirect(this.props.history, {
      pathname: `${this.props.guestContext.uri}/requested`,
      search: this.q.domain ? `?domain=${this.q.domain}` : undefined,
      state: { meetingRequest: booked },
    })
  }

  domain = (email: string) => {
    if (email) {
      return email.replace(/.*@/, '')
    }
    return null
  }

  renderBookWith = () => (
    <BookWith
      template={this.state.template}
      showBack={this.state.showBack}
      isRescheduling={this.state.isRescheduling}
      assigneeId={this.state.bookme?.bookMeeting?.assigneeId}
      assigneeName={this.state.assigneeName}
      queue={this.state.queue}
      hideBack
    />
  )

  getMarketingTitle = () => {
    const routerSettings = this.state.routerSettings || {}
    let title

    if (routerSettings) {
      if (routerSettings.wordingStyle) {
        title = (
          routerSettings.wordingStyle[this.props.locale] ||
          routerSettings.wordingStyle[defaultLocale]
        )?.bookingStyle?.body
      }
    }

    if (!title && !this.state.title) {
      return undefined
    }

    return this.state.marketing ? decodeURI(title || this.state.title) : undefined
  }

  isSingleDayEvent = () => {
    const counter = countBy(this.state.availableDays, availability => availability)

    return counter.true === 1
  }

  renderDays = () => {
    const marketingTitle = this.getMarketingTitle()

    return (
      this.state.settings.loaded && (
        <Days
          timezone={this.state.timezone}
          onDayChange={this.onDayChange}
          events={this.state.events}
          days={this.state.daysMax}
          date={this.state.date}
          startDate={this.getStartDate()}
          noMemberFound={this.state.noMemberFound}
          availableDays={this.state.availableDays}
          hideArrows={this.isSingleDayEvent()}
          titleNode={<TitleNode marketingTitle={marketingTitle} />}
        />
      )
    )
  }

  renderProspectForm(customHeader?: React.ReactNode) {
    const bookme = this.state.bookme
    const bookMeeting = bookme?.bookMeeting
    const { seriesStatus } = this.state
    const { intercomData } = this.props.guestContext

    return (
      <ThemeProviderClean>
        <WorkspaceProvider cfa id={bookMeeting?.workspaceId}>
          <ProspectFormWithValidation
            isRescheduling={this.state.isRescheduling}
            logo={this.state.logo}
            bookme={this.state.bookme?.bookMeeting ?? {}}
            assigneeName={this.state.assigneeName}
            queue={this.state.queue}
            pickHeader={!this.state.seriesId && !this.q.intercom_session}
            hideTime={seriesStatus && !seriesStatus.isGroup}
            booker={bookme?.booker}
            templateId={intercomData?.template ? intercomData.template : bookMeeting?.template}
            template={this.state.template}
            email={bookMeeting?.prospect}
            prospectId={bookMeeting?.id}
            time={this.state.time || Date.now()}
            step={this.getDuration()}
            open={this.state.confirmation}
            onConfirm={this.confirm}
            intercomData={this.props.guestContext.intercomData}
            eventTimezone={this.state.eventTimezone}
            salesforceAccountId={this.state.salesforceAccountId}
            guests={this.state.guests}
            updateGuests={(guests: string[]) => this.setState({ guests })}
            seriesId={this.state.seriesId}
            customHeader={customHeader}
          />
        </WorkspaceProvider>
      </ThemeProviderClean>
    )
  }

  renderMeetingLocationScreen() {
    const bookme = this.state.bookme
    const bookMeeting = bookme?.bookMeeting

    const className = classnames('fabe-availability-mobile', {
      'inside-intercom': this.q.intercom_session,
    })

    return (
      <div data-chili-css='page' className={className}>
        <ThemeProviderClean>
          <WorkspaceProvider cfa id={bookMeeting?.workspaceId}>
            {this.state.marketing || this.q.intercom_session ? null : this.renderBookWith()}
            <MeetingLocationPage
              bookMeetingPickedMeetingLocation={bookMeeting?.pickedMeetingLocation}
              onConfirm={this.confirmRescheduling}
              locations={this.state.template?.possibleMeetingLocations}
            />
          </WorkspaceProvider>
        </ThemeProviderClean>
      </div>
    )
  }

  renderDebug = () => {
    const renderAssignee = (assignee: any) => (
      <div>
        <span>{JSON.stringify(assignee)}</span>
      </div>
    )
    return <div>{(this.state.assigneesInfo || []).map(renderAssignee)}</div>
  }

  renderCalendar = () => {
    const className = classnames('fabe-availability-mobile', {
      'inside-intercom': this.q.intercom_session,
    })
    return (
      <div data-chili-css='page' data-test-id='Availability-calendar' className={className}>
        {this.state.debug && this.renderDebug()}
        {this.state.marketing || this.q.intercom_session ? null : this.renderBookWith()}
        {this.state.isRescheduling ? (
          <PreviousPick
            start={this.state.bookme?.bookMeeting.startDate}
            end={this.state.bookme?.bookMeeting.endDate}
            timeZone={this.state.timezone}
          />
        ) : null}
        {this.renderDays()}
        <TimeSection
          getDuration={this.getDuration}
          onZoneChange={this.onZoneChange}
          onTimeChange={this.onTimeChange}
          timezone={this.state.timezone}
          intercomData={this.props.guestContext.intercomData}
          noMemberFound={this.state.noMemberFound}
          timeLoading={this.state.timeLoading}
          date={this.state.date}
          time={this.state.time}
          events={this.state.events}
        />
      </div>
    )
  }

  render() {
    if (this.state.loadingFlower) {
      return <DotsLoader color='neutral-dark-32' height='100%' />
    }
    if (this.state.confirmation) {
      return this.renderProspectForm()
    }
    if (this.state.showOneClickBookingIntermediaryScreen) {
      return (
        <Flex alignItems='center' h='100%'>
          <OneClickBookingIntermediaryScreen
            onBook={this.bookWithIntermediaryScreen}
            bookMeeting={this.state.bookme?.bookMeeting}
            template={this.state.template}
            startDate={this.state.time}
            endDate={moment(this.state.time).add(this.getDuration(), 'minutes')}
            timeZone={this.state.timezone}
            onPickDifferentTime={
              this.state.marketing
                ? () => this.setState({ showOneClickBookingIntermediaryScreen: false })
                : undefined
            }
          />
        </Flex>
      )
    }
    if (this.state.meetingLocationScreen) {
      return this.renderMeetingLocationScreen()
    }
    return this.renderCalendar()
  }
}

const AvailabilityWithoutData = withGuestContext(AvailabilityWithoutLocale)

export default Availability

const TitleNode = ({ marketingTitle }: { marketingTitle?: string }) => {
  const [dayBookHeader] = useTranslations(['dayBookHeader'])

  return marketingTitle ? (
    <Text
      display='block'
      mt={[0, 12]}
      mx={[0, 9]}
      data-test-id='Availability-marketing-title'
      textAlign={['initial', 'center']}
    >
      {marketingTitle}
    </Text>
  ) : (
    <>{dayBookHeader}</>
  )
}
